import React from 'react';
import { BlockEmbed } from 'data/SingleArticle/types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Callout } from 'components/Callout/Callout';
import { PullQuote } from 'components/PullQuote/PullQuote';
import { ImageBlock } from 'components/ImageBlock/ImageBlock';
import { WistiaVideo } from 'components/Video/WistiaVideo';
import { YoutubeVideo } from 'components/Video/YoutubeVideo';
import { Table } from 'components/Table/Table';
import { baseMarksElementsConfiguration, richImageBody } from './configuration';
import { PersonaBasedCTA } from 'components/InArticleCTA/PersonaBasedCTA';
import { DemoCTA } from 'components/InArticleCTA/DemoCTA';
import { ExploreCTA } from 'components/InArticleCTA/ExploreCTA';
import { DownloadCTA } from 'components/InArticleCTA/DownloadCTA';
import { HowItWorksCTA } from 'components/InArticleCTA/HowItWorksCTA';
import { GenericCTA } from 'components/InArticleCTA/GenericCTA';
import { TypeformButton } from 'components/TypeformButton/TypeformButton';
import { HTMLToMarkdown } from 'components/HTMLToMarkdown/HTMLToMarkdown';
import { GuidedRecipeShort } from 'data/Article/types';
import { ZapTemplate } from 'components/ZapTemplate/ZapTemplate';
import { CollapsibleArea } from 'components/CollapsibleArea/CollapsibleArea';
import { FullWidthTextCTA } from 'components/InArticleCTA/FullWidthTextCTA';
import { EmbeddedButton } from 'components/EmbeddedButton';
import { EmbeddedFullWidthCTA } from 'components/EmbeddedFullWidthCTA';
import { EmbeddedHalfWidthCTA } from 'components/EmbeddedHalfWidthCTA';

type InArticleCTALayoutOptions =
  | 'Explore CTA'
  | 'Demo CTA'
  | 'Download CTA'
  | 'Persona CTA'
  | 'How it works CTA'
  | 'Generic CTA'
  | 'Full-width text CTA';

type InArticleStrategy = {
  [key in InArticleCTALayoutOptions]: JSX.Element;
};

export function renderBlockEmbed(
  blockEmbed: BlockEmbed,
  persona: string | null,
  guidedRecipes?: GuidedRecipeShort[],
) {
  if (blockEmbed.__typename === 'ZapTemplateComponent') {
    const { zapTemplateIDs } = blockEmbed;
    if (
      !guidedRecipes ||
      !guidedRecipes.length ||
      !zapTemplateIDs ||
      !zapTemplateIDs.length
    ) {
      return null;
    }

    const templates = guidedRecipes.filter((gr) =>
      zapTemplateIDs.some((tId) => tId === gr.id),
    );
    if (!templates.length) {
      return null;
    }

    return (
      <>
        {templates.map((template, index) => (
          <ZapTemplate key={`zap-template${index}`} template={template} />
        ))}
      </>
    );
  }

  if (blockEmbed.__typename === 'CalloutTopic') {
    const { body } = blockEmbed;

    if (!body) {
      return null;
    }

    return <Callout body={body} />;
  }

  if (blockEmbed.__typename === 'PullQuoteTopic') {
    const { quote, quoteByLine, attributionLink } = blockEmbed;

    if (!quote) {
      return null;
    }

    return (
      <PullQuote
        quote={quote}
        quoteBy={quoteByLine}
        attributionLink={attributionLink}
      />
    );
  }

  if (blockEmbed.__typename === 'Collapsible') {
    const { content, title, openByDefault } = blockEmbed;
    return (
      <CollapsibleArea
        content={content}
        title={title}
        openByDefault={openByDefault || false}
      />
    );
  }

  if (blockEmbed.__typename === 'RichImageAsset') {
    const {
      image,
      describedImage,
      longDescribedImage,
      maxWidth,
      caption,
      hyperlink,
      newTab,
    } = blockEmbed;
    const url = image?.url || '';
    const title = image?.title || '';
    const alt = longDescribedImage || describedImage || '';

    if (!image || !url) {
      return null;
    }

    const formattedCaption = documentToReactComponents(caption?.json, {
      renderNode: richImageBody,
      renderMark: baseMarksElementsConfiguration,
    });

    return (
      <ImageBlock
        hyperlink={hyperlink}
        isNewTab={Boolean(newTab)}
        image={{ maxWidth, alt, title, url }}
      >
        {formattedCaption}
      </ImageBlock>
    );
  }

  if (blockEmbed.__typename === 'VideoAsset') {
    const { videoId, videoProvider } = blockEmbed;

    if (
      !videoId ||
      (videoProvider?.name?.toLowerCase() !== 'wistia' &&
        videoProvider?.name?.toLowerCase() !== 'youtube')
    ) {
      return null;
    }

    if (videoProvider?.name === 'Wistia') {
      return <WistiaVideo videoId={videoId} />;
    }

    return <YoutubeVideo videoId={videoId} />;
  }

  if (blockEmbed.__typename === 'Table') {
    const { table } = blockEmbed;

    if (!table) {
      return null;
    }

    return <Table data={table.tableData} />;
  }

  if (blockEmbed.__typename === 'SectionAnchorAssembly') {
    const { htmlId } = blockEmbed;

    if (!htmlId) {
      return null;
    }

    return <div id={htmlId} />;
  }

  if (blockEmbed.__typename === 'Typeform') {
    const { buttonColor, buttonText, typeformUrl } = blockEmbed;

    if (!typeformUrl) {
      return null;
    }
    const urlParts = typeformUrl.split('/');
    const id = urlParts[urlParts.length - 1];

    if (!id) {
      return null;
    }

    return (
      <TypeformButton buttonColor={buttonColor} id={id} name={buttonText} />
    );
  }

  if (blockEmbed.__typename === 'Markdown') {
    const { markdown: htmlString } = blockEmbed;

    if (!htmlString) return null;

    return <HTMLToMarkdown html={htmlString} />;
  }

  if (blockEmbed.__typename === 'InArticleCtaContainer') {
    const { fallbackCta: inArticleCta, variantsCtaCollection } = blockEmbed;

    if (
      !inArticleCta?.headline ||
      !inArticleCta.buttonText ||
      !inArticleCta.buttonUrl ||
      !inArticleCta.layout
    ) {
      return null;
    }

    /**
     * We're supposed to use Variants only for Persona based In Article CTAs
     * So, we assume that all the data will be persona related and we render the Persona InArticleCTA component.
     */
    if (variantsCtaCollection?.items.length) {
      const foundVariant = variantsCtaCollection.items.find(
        (item) => item?.persona?.slug === persona,
      );

      return (
        <PersonaBasedCTA
          inArticleCTA={foundVariant?.inArticleCta ?? inArticleCta}
        />
      );
    }

    const layout = inArticleCta.layout as InArticleCTALayoutOptions;

    const CTAComponentsMap: InArticleStrategy = {
      'How it works CTA': <HowItWorksCTA inArticleCTA={inArticleCta} />,
      'Download CTA': <DownloadCTA inArticleCTA={inArticleCta} />,
      'Explore CTA': <ExploreCTA inArticleCTA={inArticleCta} />,
      'Demo CTA': <DemoCTA inArticleCTA={inArticleCta} />,
      'Persona CTA': <PersonaBasedCTA inArticleCTA={inArticleCta} />,
      'Generic CTA': <GenericCTA inArticleCTA={inArticleCta} />,
      'Full-width text CTA': <FullWidthTextCTA inArticleCTA={inArticleCta} />,
    };

    return CTAComponentsMap[layout] || null;
  }

  if (blockEmbed.__typename === 'Button') {
    const { alignment, color, text, url } = blockEmbed;

    if (!text || !url) {
      return null;
    }

    return (
      <EmbeddedButton
        alignment={alignment}
        color={color}
        text={text}
        url={url}
      />
    );
  }

  if (blockEmbed.__typename === 'FullWidthCta') {
    const props = {
      ...(blockEmbed.heading && { heading: blockEmbed.heading }),
      ...(blockEmbed.subheading && { subheading: blockEmbed.subheading }),
      ...(blockEmbed.body && { body: blockEmbed.body }),
      ...(blockEmbed.buttonText && { buttonText: blockEmbed.buttonText }),
      ...(blockEmbed.buttonUrl && { buttonUrl: blockEmbed.buttonUrl }),
      // If both Contentful and Cloudinary images are provided, we want to give the Cloudinary image priority.
      ...(blockEmbed.contentfulImage?.url &&
        blockEmbed.contentfulImage.description && {
          imageUrl: blockEmbed.contentfulImage.url,
          imageAltText: blockEmbed.contentfulImage.description,
        }),
      ...(blockEmbed.cloudinaryImage?.file?.[0]?.url &&
        blockEmbed.cloudinaryImage.description && {
          imageUrl: blockEmbed.cloudinaryImage.file[0].url,
          imageAltText: blockEmbed.cloudinaryImage.description,
        }),
      ...(blockEmbed.imageSize && { imageSize: blockEmbed.imageSize }),
    };

    return <EmbeddedFullWidthCTA {...props} />;
  }

  if (blockEmbed.__typename === 'HalfWidthCta') {
    const props = {
      ...(blockEmbed.heading && { heading: blockEmbed.heading }),
      ...(blockEmbed.subheading && { subheading: blockEmbed.subheading }),
      ...(blockEmbed.buttonText && { buttonText: blockEmbed.buttonText }),
      ...(blockEmbed.buttonUrl && { buttonUrl: blockEmbed.buttonUrl }),
    };

    return <EmbeddedHalfWidthCTA {...props} />;
  }

  /**
   * Render `null` if we don't find a matching component.
   */
  return null;
}
